import { useMemo, useState } from 'react'
import {
  BadgeDollarSign,
  BadgeInfo,
  CheckCircle2,
  Eye,
  LoaderCircle,
  Shield,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react'
import { useActionAuditLog, useActions } from '../../hooks/useCompetitiveData'
import { Skeleton } from './Skeleton'
import { getSeverityClasses } from './severityStyles'
import type { Action, ActionSummary } from '../../types/competitive'

interface ActionLogPanelProps {
  competitorName?: string
  actions?: Array<ActionSummary | Action>
  compact?: boolean
}

function actionIcon(actionType: string) {
  const normalized = actionType.toLowerCase()
  if (normalized.includes('price') || normalized.includes('deal')) {
    return <BadgeDollarSign className="h-4 w-4" />
  }
  if (normalized.includes('creator')) {
    return <Users className="h-4 w-4" />
  }
  if (normalized.includes('keyword') || normalized.includes('trademark')) {
    return <Shield className="h-4 w-4" />
  }
  if (normalized.includes('ai')) {
    return <Sparkles className="h-4 w-4" />
  }
  return <BadgeInfo className="h-4 w-4" />
}

function toLabel(actionType: string): string {
  return actionType
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function fallbackSummary(action: Action): string {
  return action.finding || action.action_description || 'Action completed without additional findings.'
}

function coerceSummaryAction(action: ActionSummary | Action): Action {
  return {
    id: action.id,
    trigger_type: 'trigger_type' in action ? action.trigger_type : '',
    trigger_id: 'trigger_id' in action ? action.trigger_id : null,
    trigger_severity: 'trigger_severity' in action ? action.trigger_severity : '',
    trigger_summary: 'trigger_summary' in action ? action.trigger_summary : '',
    action_type: action.action_type,
    action_description:
      'action_description' in action && action.action_description
        ? action.action_description
        : action.finding || 'Action executed.',
    status: action.status,
    finding: action.finding,
    finding_severity: action.finding_severity ?? null,
    recommendation: action.recommendation ?? null,
    competitor_name: action.competitor_name ?? null,
    competitor_id: action.competitor_id ?? null,
    executed_at: action.executed_at,
    created_at: action.created_at || action.executed_at || new Date().toISOString(),
    updated_at: action.updated_at || action.executed_at || new Date().toISOString(),
    brief_date: 'brief_date' in action ? action.brief_date ?? null : null,
    action_text: 'action_text' in action ? action.action_text : undefined,
    completed_at: 'completed_at' in action ? action.completed_at : undefined,
    legacy_status: 'legacy_status' in action ? action.legacy_status : undefined,
  }
}

function ActionAuditTrail({ actionId, open }: { actionId: number; open: boolean }) {
  const { data, isLoading, error } = useActionAuditLog(open ? actionId : 0)
  const rows = data?.audit_log ?? []
  const errorMessage = error instanceof Error ? error.message : null

  if (!open) {
    return null
  }

  if (isLoading) {
    return (
      <div className="mt-2 rounded-md border border-slate-200 bg-white p-2">
        <Skeleton height="h-3" className="w-32" />
        <Skeleton height="h-3" className="mt-2 w-full" />
      </div>
    )
  }

  if (errorMessage) {
    return <p className="mt-2 text-xs text-red-700">Unable to load audit trail: {errorMessage}</p>
  }

  if (rows.length === 0) {
    return <p className="mt-2 text-xs text-slate-500">No audit trail steps captured for this action.</p>
  }

  return (
    <div className="mt-2 space-y-1.5 rounded-md border border-slate-200 bg-white p-2">
      {rows.map((row) => (
        <div key={row.id} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
          <p className="font-semibold text-slate-800">
            Step {row.step_number}: {row.step_description}
          </p>
          {row.step_result ? <p className="mt-1 text-slate-600">{row.step_result}</p> : null}
        </div>
      ))}
    </div>
  )
}

export default function ActionLogPanel({
  competitorName,
  actions: inlineActions = [],
  compact = false,
}: ActionLogPanelProps) {
  const [expandedActionId, setExpandedActionId] = useState<number | null>(null)
  const shouldFetch = inlineActions.length === 0
  const { data, isLoading, isFetching } = useActions(competitorName, 50, shouldFetch)

  const resolvedActions = useMemo(() => {
    const fetched = (data?.actions ?? []) as Action[]
    if (fetched.length > 0) {
      return fetched
    }

    return inlineActions.map((entry) => coerceSummaryAction(entry))
  }, [data?.actions, inlineActions])

  const completedCount = useMemo(
    () => resolvedActions.filter((action) => ['completed', 'done'].includes(action.status.toLowerCase())).length,
    [resolvedActions]
  )

  if (isLoading && !resolvedActions.length) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <Skeleton height="h-3" className="w-44" />
        <div className="mt-2 space-y-2">
          <Skeleton height={compact ? 'h-9' : 'h-14'} />
          <Skeleton height={compact ? 'h-9' : 'h-14'} />
        </div>
      </div>
    )
  }

  if (!resolvedActions.length) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        No automated actions yet. Actions trigger when a competitor&apos;s BSR jumps 5x+ or creator mentions spike.
      </div>
    )
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          ✅ Actions Taken ({completedCount} of {resolvedActions.length} complete)
        </p>
        {isFetching ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <LoaderCircle className="h-3 w-3 animate-spin" />
            Refreshing...
          </span>
        ) : null}
      </div>

      <div className="mt-2 space-y-2">
        {resolvedActions.map((action) => {
          const status = action.status || 'pending'
          const shortFinding = fallbackSummary(action)
          const isExpanded = expandedActionId === action.id
          const statusColors = getSeverityClasses(status)
          const findingSeverityColors = getSeverityClasses(action.finding_severity)

          if (compact) {
            return (
              <article key={action.id} className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      <span className="text-slate-500">{actionIcon(action.action_type)}</span>
                      <span className="truncate">{toLabel(action.action_type)}</span>
                    </div>
                    <p className="line-clamp-1 text-xs text-slate-600">{shortFinding}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${statusColors.badge} ${statusColors.border}`}
                  >
                    {status}
                  </span>
                </div>
              </article>
            )
          }

          return (
            <article key={action.id} className="rounded-md border border-slate-200 bg-white p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                    <span className="text-slate-500">{actionIcon(action.action_type)}</span>
                    <span className="truncate">{toLabel(action.action_type)}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-600">{shortFinding}</p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span
                    className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${statusColors.badge} ${statusColors.border}`}
                  >
                    {status}
                  </span>
                  {action.finding_severity ? (
                    <span
                      className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${findingSeverityColors.badge} ${findingSeverityColors.border}`}
                    >
                      {action.finding_severity.replace(/_/g, ' ')}
                    </span>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
                onClick={() => setExpandedActionId(isExpanded ? null : action.id)}
              >
                {isExpanded ? <XCircle className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {isExpanded ? 'Hide Audit Trail' : 'View Audit Trail'}
              </button>

              {isExpanded && (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                  <p className="whitespace-pre-wrap">{action.finding || action.action_description || 'No finding captured.'}</p>
                  <p className="mt-2 text-slate-600">
                    <span className="font-semibold">Recommendation:</span>{' '}
                    {action.recommendation || 'No recommendation captured.'}
                  </p>
                  {action.status.toLowerCase() === 'completed' && action.executed_at ? (
                    <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Executed {new Date(action.executed_at).toLocaleString()}
                    </p>
                  ) : null}
                  <ActionAuditTrail actionId={action.id} open={isExpanded} />
                </div>
              )}
            </article>
          )
        })}
      </div>
    </div>
  )
}
